<template>
  <button
    :class="baseButtonClasses"
    v-bind="$attrs"
    :aria-label="accessibilityLabel"
    :disabled="isDisabled"
    :type="[isSubmit ? 'submit' : '']"
    class="base-button"
    v-on="$listeners"
  >
    <slot />
  </button>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  accessibilityLabel: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["x-small", "small", "medium", "large"].includes(value),
  },
  shape: {
    type: String,
    default: "rectangle",
    validator: (value) => ["rectangle", "square", "circle"].includes(value),
  },
  type: {
    type: String,
    default: "secondary",
    validator: (value) => [
      "primary",
      "secondary",
      "success",
      "warning",
      "danger",
      "error",
      "info",
    ].includes(value),
  },
  variation: {
    type: String,
    default: "basic",
    validator: (value) => ["basic", "ghost", "outline"].includes(value),
  },
  mode: {
    type: String,
    default: "light",
    validator: (value) => ["light", "dark"].includes(value),
  },
  cap: {
    type: [String, Array],
    default: "none",
    validator: (value) => {
      const validTypes = [
        "none",
        "top",
        "right",
        "bottom",
        "left",
        "top-left",
        "top-right",
        "bottom-right",
        "bottom-left",
      ];

      if (Array.isArray(value)) {
        return value.some((type) => validTypes.includes(type));
      } else {
        return validTypes.includes(value);
      }
    },
  },
  isFullWidth: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isSubmit: {
    type: Boolean,
    default: false,
  },
});

const baseButtonClasses = computed(() => ({
  [`base-button--type-${props.type}`]: true,
  [`base-button--variation-${props.variation}`]: true,
  [`base-button--mode-${props.mode}`]: true,
  [`base-button--size-${props.size}`]: true,
  [`base-button--shape-${props.shape}`]: true,
  [`base-button--cap-${props.cap}`]: true,
  "base-button--is-full-width": props.isFullWidth,
  "base-button--is-loading": props.isLoading,
  "base-button--is-disabled": props.isDisabled,
}));
</script>

<style lang="scss" scoped>
@import "./base-button.scss";
</style>
